import { useTranslation } from '@wix/fe-essentials/i18n';
import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';
import { isReorganizingDomOrderAtom } from '../../atoms/session-atoms';
import { BiCtaNames, BiModalTypes } from '../../bi/types';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { buildStudioDialogProps } from '../modals/build-studio-dialog-props';
import { OrganizeConfirmationModalContent } from './organize-confirmation-modal-content';
export const useDomOrderOrganizeConfirmationModal = () => {
  const {
    a11yWizardAdapter,
    capabilities,
    editorHost
  } = useA11yWizardAdapter();
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const setIsReorganizingDomOrder = useSetAtom(isReorganizingDomOrderAtom);
  const showOrganizeConfirmationModal = useCallback(() => {
    const isSiteLanguageRTL = a11yWizardAdapter.getIsSiteLanguageRTL();
    const needsDirectionUpdate = !a11yWizardAdapter.getIsSiteDirectionMatchingLanguageDirection();
    const modalType = isSiteLanguageRTL ? BiModalTypes.SettingDomOrderForRTL : BiModalTypes.SettingDomOrderForLTR;
    return new Promise(async resolve => {
      setIsReorganizingDomOrder(true);
      biLogger.modalView({
        modalType
      });
      const content = /*#__PURE__*/React.createElement(OrganizeConfirmationModalContent, {
        compactPanelChrome: capabilities.compactPanelChrome,
        isSiteLanguageRTL: isSiteLanguageRTL,
        t: t,
        onLearnMoreClick: () => {
          openSelectedArticle(editorHost, needsDirectionUpdate ? 'DOM.updateDirectionModal.helpID' : 'editor-package-accessibility-wizard.articles-organizing-DOM-help');
        }
      });
      const modalProps = {
        ...getOrganizeConfirmationModalProps(t, content),
        onCloseButtonClick: () => {
          biLogger.modalCtaClick({
            ctaName: BiCtaNames.X,
            modalType
          });
          setIsReorganizingDomOrder(false);
          resolve('cancel');
        },
        primaryButtonOnClick: async () => {
          biLogger.modalCtaClick({
            ctaName: BiCtaNames.OrganizeContent,
            modalType
          });
          try {
            await a11yWizardAdapter.reorganizeAllPagesDOMOrder(true, true);
            await a11yWizardAdapter.enableSiteAutoDOMOrder();
          } catch (e) {
            setIsReorganizingDomOrder(false);
          }
          resolve('primary');
        },
        secondaryButtonOnClick: () => {
          biLogger.modalCtaClick({
            ctaName: BiCtaNames.Cancel,
            modalType
          });
          setIsReorganizingDomOrder(false);
          resolve('cancel');
        }
      };
      await a11yWizardAdapter.showDialog(...buildStudioDialogProps(modalProps));
    });
  }, [biLogger, t, a11yWizardAdapter, capabilities.compactPanelChrome, openSelectedArticle, editorHost, setIsReorganizingDomOrder]);
  return {
    showOrganizeConfirmationModal
  };
};
export const getOrganizeConfirmationModalProps = (t, content) => ({
  content,
  primaryButtonText: t('accessibility-wizard.footer.dom-order.organize-confirmation-modal.primary-button'),
  secondaryButtonText: t('accessibility-wizard.footer.dom-order.organize-confirmation-modal.secondary-button'),
  skin: 'standard',
  title: t('accessibility-wizard.footer.dom-order.organize-confirmation-modal.title')
});
//# sourceMappingURL=dom-order-organize-confirmation-modal.js.map